import { useDispatch, useSelector } from "react-redux";
import ColorizeIcon from "@mui/icons-material/Colorize";
import { selectSelectedTake3D } from "../../../app/slices/TrainingSlice";
import {
  ISetCamera,
  requiredCameraProperties,
} from "../../../models/interactions/SetCamera";
import { SequenceNodeProps } from "./SequenceNodeProps";
import { useMemo } from "react";
import { selectEnvironmentInteractionPoints } from "../../../app/slices/EnvironmentSlice";
import useSituationHelper from "../../../hooks/SituationHelper.hook";
import NodeStringDropdown from "../components/NodeStringDropdown";
import useUpdateSequenceInteraction from "./UpdateSequenceInteraction.hook";
import NodeToggle from "../components/NodeToggle";
import NodeNumberField from "../components/NodeNumberField";
import { UpdatePropertyOfInteraction } from "../../../app/slices/helpers/TrainingSliceHelper";
import { InteractionProperty } from "../../../models/InteractionProperty";
import {
  openSequencePositionPicker,
  selectSequencePositionPicker,
} from "../../../app/slices/GlobalSlice";
import {
  addEnvironmentPositionDisplayNames,
  getEnvironmentPositionContexts,
} from "../EnvironmentPositionContext";

export default function SetCameraNodePreview(props: SequenceNodeProps) {
  const setCamera = props.interaction as ISetCamera;
  const currentTake = useSelector(selectSelectedTake3D);
  const { updateInteraction } = useUpdateSequenceInteraction();
  const dispatch = useDispatch();
  const sequencePositionPicker = useSelector(selectSequencePositionPicker);

  const { getSituationMetadata } = useSituationHelper();
  const ips = useSelector(selectEnvironmentInteractionPoints);

  const camerasMapped = useMemo(() => {
    const contexts = getEnvironmentPositionContexts(
      ips ?? [],
      currentTake?.situations,
      getSituationMetadata,
    );

    return addEnvironmentPositionDisplayNames(
      contexts.flatMap((context) =>
        context.cameraPositions.map((camera) => ({
          id: camera.id,
          sourceId: camera.id,
          name: camera.name,
          context,
        })),
      ),
    );
  }, [currentTake?.situations, getSituationMetadata, ips]);

  const isPickerActiveForNode =
    sequencePositionPicker.isOpen &&
    sequencePositionPicker.nodeId === props.nodeId &&
    sequencePositionPicker.positionType === "camera";

  const cameraDropdown = useMemo(() => {
    return (
      <div className="flex flex-col gap-[8px] noDrag nowheel">
        <div className="font-medium text-creator-text text-[14px]">Camera</div>
        <div className="flex items-center gap-[8px]">
          <div className="flex-1 min-w-0">
            <NodeStringDropdown
              items={camerasMapped.map((c) => c.displayName)}
              selectedItem={
                camerasMapped.find(
                  (c) =>
                    c.id === setCamera.cameraId &&
                    (!setCamera.takeLocationId ||
                      c.context.takeLocationId === setCamera.takeLocationId),
                )?.displayName ??
                camerasMapped.find((c) => c.id === setCamera.cameraId)
                  ?.displayName ??
                "Not found"
              }
              onClick={(cameraName) => {
                const selectedCamera = camerasMapped.find(
                  (c) => c.displayName === cameraName,
                );
                updateInteraction(props.nodeId, {
                  ...setCamera,
                  cameraId: selectedCamera?.id ?? "",
                  takeLocationId: selectedCamera?.context.takeLocationId ?? "",
                } as ISetCamera);
              }}
            />
          </div>
          <button
            type="button"
            aria-label="Pick on map"
            title="Pick on map"
            data-testid="pick-camera-on-map-button"
            className={
              "group flex h-[32px] w-[32px] items-center justify-center rounded-full border-[2px] transition-all duration-150 " +
              (isPickerActiveForNode
                ? "border-creator-primary bg-creator-primary text-white shadow-[0_0_0_4px_rgba(58,196,255,0.18)]"
                : "border-creator-primary bg-creator-primary-background text-creator-primary hover:bg-creator-primary-washed")
            }
            onClick={() =>
              dispatch(
                openSequencePositionPicker({
                  nodeId: props.nodeId,
                  positionType: "camera",
                  title: "Pick camera position",
                  presentation: "splitView",
                }),
              )
            }
          >
            <ColorizeIcon fontSize="inherit" className="text-[18px]" />
          </button>
        </div>
      </div>
    );
  }, [
    camerasMapped,
    dispatch,
    isPickerActiveForNode,
    props.nodeId,
    setCamera,
    updateInteraction,
  ]);

  const instantProperty = useMemo(() => {
    return setCamera.properties?.find((p) => p.id === "instant");
  }, [setCamera.properties]);
  const speedProperty = useMemo(() => {
    return setCamera.properties?.find((p) => p.id === "speed");
  }, [setCamera.properties]);
  const isInstant = (instantProperty?.value as boolean) ?? false;

  const instantToggle = useMemo(
    () => (
      <NodeToggle
        label="Instant"
        value={isInstant}
        onChange={(instant) => {
          if (instantProperty) {
            const updatedProperty: InteractionProperty = {
              ...instantProperty,
              value: instant,
            };
            const updatedInteraction = UpdatePropertyOfInteraction(
              setCamera,
              updatedProperty,
            );
            updateInteraction(props.nodeId, updatedInteraction);
          }
        }}
      />
    ),
    [instantProperty, isInstant, setCamera, updateInteraction, props.nodeId],
  );

  const speedInput = useMemo(
    () => (
      <NodeNumberField
        label="Speed"
        value={(speedProperty?.value as number) ?? 1}
        disabled={isInstant}
        onChange={(speed) => {
          const baseProperty =
            speedProperty ??
            requiredCameraProperties.find(
              (property) => property.id === "speed",
            );
          if (!baseProperty) return;

          const updatedProperty: InteractionProperty = {
            ...baseProperty,
            value: speed,
          };

          const updatedInteraction = speedProperty
            ? UpdatePropertyOfInteraction(setCamera, updatedProperty)
            : ({
                ...setCamera,
                properties: [...(setCamera.properties ?? []), updatedProperty],
              } as ISetCamera);

          updateInteraction(props.nodeId, updatedInteraction);
        }}
      />
    ),
    [isInstant, props.nodeId, setCamera, speedProperty, updateInteraction],
  );

  return (
    <div className="flex flex-col w-full h-full gap-[12px] noDrag">
      {cameraDropdown}
      {instantToggle}
      {speedInput}
    </div>
  );
}
